/* Standard KPI styles */
.kpi {
  display: flex;
  flex-direction: column;
  gap: var(--mantine-spacing-xs);
  justify-content: center;
  height: 100%;
  padding: var(--mantine-spacing-md);
}

.value-row {
  display: flex;
  gap: var(--mantine-spacing-xs);
  align-items: baseline;
}

.value {
  font-size: clamp(var(--mantine-spacing-lg), var(--mantine-spacing-md) + 6cqi, var(--mantine-spacing-xl));
  font-weight: var(--mantine-heading-font-weight);
  line-height: 1.1;
  color: var(--mantine-color-text);
}

.unit {
  font-size: clamp(var(--mantine-spacing-sm), calc(var(--mantine-spacing-md) * 0.5) + 2cqi, var(--mantine-spacing-lg));
  color: var(--mantine-color-dark-2);
}

.label {
  font-size: clamp(var(--mantine-spacing-sm), calc(var(--mantine-spacing-md) * 0.5) + 2cqi, var(--mantine-spacing-md));
  line-height: 1.3;
  color: var(--mantine-color-dark-2);
}

.trend-row {
  display: flex;
  gap: var(--mantine-spacing-xs);
  align-items: center;
  margin-top: var(--mantine-spacing-xs);
}

.trend-icon {
  flex-shrink: 0;
}

.trend-value {
  font-size: clamp(
    var(--mantine-spacing-sm),
    calc(var(--mantine-spacing-md) * 0.5) +
    1.5cqi,
    var(--mantine-spacing-md)
  );
  font-weight: calc(var(--mantine-heading-font-weight) * 0.714);
}

.trend-up {
  color: var(--mantine-color-green-5);
}

.trend-down {
  color: var(--mantine-color-red-5);
}

.trend-neutral {
  color: var(--mantine-color-dark-3);
}

/* Container queries for tall cells - grid-cell context from block-tokens.module.css */
@container grid-cell (min-height: 200px) {
  .kpi {
    padding-block: var(--mantine-spacing-lg);
  }
}

@container grid-cell (min-height: 300px) {
  .value {
    font-size: clamp(var(--mantine-spacing-xl), 1.5rem + 8cqi, 4rem);
  }
}

/* Alert-style KPI styles */
@property --fac {
  syntax: '<number>';
  inherits: true;
  initial-value: 0;
}

@keyframes pulse {
  0% {
    --fac: 0;
  }
  10% {
    --fac: 1;
  }
  100% {
    --fac: 0;
  }
}

.alert-label-container {
  display: flex;
  flex-flow: row wrap;
  grid-area: header;
  column-gap: var(--mantine-spacing-xs);
  align-items: baseline;
  justify-content: flex-end;
  padding-inline: var(--padding);
  overflow: hidden;
  text-align: right;
}

.alert-label {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 80%;
  white-space: nowrap;
}

.alert-value-container {
  display: flex;
  grid-area: content;
  column-gap: var(--mantine-spacing-xs);
  align-items: baseline;
  justify-content: flex-end;
  padding-inline: var(--padding);
  white-space: nowrap;
}

.alert-value {
  font-size: 180%;
  font-weight: var(--mantine-heading-font-weight);
}

.alert-unit {
  font-size: 60%;
  opacity: 0.6;
  &::before {
    content: '(';
  }
  &::after {
    content: ')';
  }
}

.alert-icon {
  grid-area: icon;
  svg {
    display: block;
  }
}

.alert {
  --glow-scale: 1;
  --alert-color: color-mix(in oklab, var(--mantine-color-blue-6), #000 60%);
  --alert-secondary-color: color-mix(in oklab, var(--mantine-color-blue-6), #000 60%);
  --overlay-color: color-mix(in oklab, var(--alert-color), var(--alert-secondary-color) 50%);
  --padding: var(--mantine-spacing-sm);
  position: relative;
  display: grid;
  grid-template:
    'icon header chart' auto
    'icon content chart' auto / auto 1fr auto;
  row-gap: calc(var(--mantine-spacing-xs) * 0.5);
  place-content: center;
  align-items: center;
  line-height: 1.1;
  color: var(--mantine-color-white);
  text-decoration: none;
  text-shadow: 0 1px 3px #0005;
  background: var(--alert-color);
  border-radius: var(--mantine-radius-xs);
  transition: background 0.1s ease-out;

  &[href] {
    &:hover {
      background: oklch(from var(--alert-color) calc(l * 0.8) c h);
    }
  }

  &::before {
    position: absolute;
    inset: 0;
    z-index: 10;

    pointer-events: none;
    content: '';
    background: radial-gradient(
      ellipse 100% 100% at 30% 0%,
      color-mix(in oklab, var(--overlay-color), transparent 30%),
      color-mix(in oklab, var(--overlay-color), transparent 100%)
    );
    border-radius: inherit;
    mix-blend-mode: screen;
  }

  &::after {
    position: absolute;
    inset: 0;
    z-index: 11;
    padding: 1px;
    pointer-events: none;
    content: '';
    background: linear-gradient(175deg, #fffa 0%, #0001 60%, #000a 100%);
    border-radius: inherit;
    mix-blend-mode: overlay;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
  }

  &.yellow {
    --alert-color: var(--mantine-color-orange-7);
    --alert-secondary-color: var(--mantine-color-yellow-5);
  }
  &.orange {
    --alert-color: var(--mantine-color-orange-7);
    --alert-secondary-color: var(--mantine-color-orange-5);
  }
  &.red {
    --alert-color: var(--mantine-color-red-7);
    --alert-secondary-color: var(--mantine-color-red-5);
  }
  &.blue {
    --alert-color: var(--mantine-color-blue-9);
    --alert-secondary-color: var(--mantine-color-blue-5);
  }
  &.green {
    --alert-color: var(--mantine-color-green-9);
    --alert-secondary-color: var(--mantine-color-green-5);
  }

  &.corrective {
    --alert-color: var(--mantine-color-orange-7);
    --alert-secondary-color: var(--mantine-color-yellow-5);
  }

  &.preventive {
    --alert-color: var(--mantine-color-blue-9);
    --alert-secondary-color: var(--mantine-color-blue-7);
  }

  &.emergency {
    --fac: 1;
    --alert-color: var(--mantine-color-red-7);
    --alert-secondary-color: var(--mantine-color-orange-5);
    &::before {
      box-shadow:
        0 0 calc(calc(var(--mantine-spacing-md) * 0.2) * var(--fac)) calc(0.1rem * var(--fac))
        color-mix(in oklab, var(--mantine-color-red-5), #f000 50%),
        0 0 calc(var(--mantine-spacing-xl) * var(--fac)) calc(var(--mantine-spacing-md) * var(--fac))
        color-mix(in oklab, var(--mantine-color-red-9), #f00a 80%);
      animation: pulse 3s ease-out infinite;
    }
    svg {
      position: absolute;
      top: 50%;
      left: var(--mantine-spacing-md);
      color: var(--mantine-color-red-3);
      translate: 0 -50%;
    }
  }
}
